import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

export interface LeaderboardData {
  firstName: string;
  lastName: string;
  newContractsPastMonth: number;
  totalNewContractsPastMonth: number;
  contractsValuePastMonth: number;
  numberOfActiveReservations: number;
  conversionRateLast365Days: number;
}

export interface ProductReportData {
  productName: string;
  productCode: string;
  rollingMonths: {
    month: string;
    numberOfContracts: number;
    valueOfContracts: number;
    contractCountByStatus?: {
      DRAFT?: number;
      ACTIVE?: number;
      EXPIRED?: number;
      REJECTED?: number;
      TERMINATED?: number;
    };
  }[];
  totalSalesCount?: number;
  totalContractValue?: number; // Only ACTIVE contracts
  contractCountByStatus?: {
    DRAFT?: number;
    ACTIVE?: number;
    EXPIRED?: number;
    REJECTED?: number;
    TERMINATED?: number;
  };
}

export interface TeamActivityData {
  firstName: string;
  lastName: string;
  dailyInteractions: { date: string; count: number }[];
  totalPast7Days: number;
}

export interface PipelineAlertData {
  firstName: string;
  lastName: string;
  numberOfPendingReservations: number;
  numberOfInterestedReservations: number;
  numberOfActiveReservations: number; // Total of pending + interested (for backward compatibility)
  numberOfExpiringNext7Days: number;
}

export interface FSRPerformanceData {
  firstName: string;
  lastName: string;
  wins: number;
  losses: number;
  totalValueWins: number;
  interactionsPerWin: number;
  avgInteractionsPerWeek: number;
  longTermTrend: number;
  topProducts: { productName: string; share: number }[];
}

export interface PersonalPipelineData {
  reservationId: string;
  customer: string;
  product: string;
  status: string;
  createdAt: Date;
  validUntil: Date;
}

export interface MonthlyWinLossData {
  reservationId: string;
  customer: string;
  product: string;
  status: string;
  closedOn: Date;
  contractValue: number;
}

export interface PersonalPerformanceData {
  winLossRatio: number;
  interactionsPerWin: number;
  numberOfInteractionsPerWeek: number;
  numberOfNewContracts: number;
  valueOfNewContracts: number;
}

@Injectable({
  providedIn: 'root'
})
export class StaticReportService {
  private readonly apiUrl = `${environment.apiUrl}/static-reports`;

  constructor(private http: HttpClient) {}

  /**
   * Get Leaderboard Report
   */
  async getLeaderboard(
    month: number,
    year: number,
    page: number = 1,
    limit: number = 100,
    subscriberId?: string
  ): Promise<{ data: LeaderboardData[]; total: number; pagination: any; period: any }> {
    let params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString())
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (subscriberId) {
      params = params.set('selectedSubscriberId', subscriberId);
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ data: LeaderboardData[]; total: number; pagination: any; period: any }>>(
        `${this.apiUrl}/leaderboard`,
        { params }
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get leaderboard report');
    }

    return response.data;
  }

  /**
   * Get Product Report
   */
  async getProductReport(subscriberId?: string, productId?: string): Promise<ProductReportData[]> {
    let params = new HttpParams();
    if (subscriberId) {
      params = params.set('selectedSubscriberId', subscriberId);
    }
    if (productId) {
      params = params.set('productId', productId);
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<ProductReportData[]>>(
        `${this.apiUrl}/product`,
        { params }
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get product report');
    }

    return response.data;
  }

  /**
   * Get Team Activity Report
   */
  async getTeamActivity(
    page: number = 1,
    limit: number = 100,
    subscriberId?: string
  ): Promise<{ data: TeamActivityData[]; total: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (subscriberId) {
      params = params.set('selectedSubscriberId', subscriberId);
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ data: TeamActivityData[]; total: number }>>(
        `${this.apiUrl}/team-activity`,
        { params }
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get team activity report');
    }

    return response.data;
  }

  /**
   * Get Pipeline Alert Report
   */
  async getPipelineAlert(
    page: number = 1,
    limit: number = 100,
    subscriberId?: string
  ): Promise<{ data: PipelineAlertData[]; total: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (subscriberId) {
      params = params.set('selectedSubscriberId', subscriberId);
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ data: PipelineAlertData[]; total: number }>>(
        `${this.apiUrl}/pipeline-alert`,
        { params }
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get pipeline alert');
    }

    return response.data;
  }

  /**
   * Get FSR Team Performance Review
   */
  async getFSRTeamPerformance(
    startDate?: Date,
    endDate?: Date,
    page: number = 1,
    limit: number = 100,
    subscriberId?: string
  ): Promise<{ data: FSRPerformanceData[]; total: number; period: any }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }

    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }

    if (subscriberId) {
      params = params.set('selectedSubscriberId', subscriberId);
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ data: FSRPerformanceData[]; total: number; period: any }>>(
        `${this.apiUrl}/fsr-team-performance`,
        { params }
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get FSR team performance');
    }

    return response.data;
  }

  /**
   * Get Personal Pipeline Report
   * Uses POST method with request body
   */
  async getPersonalPipeline(
    type: 'active' | 'expired',
    page: number = 1,
    limit: number = 100,
    fsrId?: string,
    selectedDate?: Date,
    selectedSubscriberId?: string
  ): Promise<{ data: PersonalPipelineData[]; total: number }> {
    const body: any = {
      type,
      page,
      limit
    };

    if (fsrId) {
      body.fsrId = fsrId;
    }

    if (selectedDate) {
      body.selectedDate = selectedDate.toISOString();
    }

    if (selectedSubscriberId) {
      body.selectedSubscriberId = selectedSubscriberId;
    }

    const response = await firstValueFrom(
      this.http.post<ApiResponse<{ data: PersonalPipelineData[]; total: number }>>(
        `${this.apiUrl}/personal-pipeline`,
        body
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get personal pipeline');
    }

    return response.data;
  }

  /**
   * Get Monthly Win/Loss Report
   */
  async getMonthlyWinLoss(
    month: number,
    year: number,
    type: 'wins' | 'losses',
    page: number = 1,
    limit: number = 100,
    fsrId?: string
  ): Promise<{ data: MonthlyWinLossData[]; total: number; period: any }> {
    let params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString())
      .set('type', type)
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (fsrId) {
      params = params.set('fsrId', fsrId);
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ data: MonthlyWinLossData[]; total: number; period: any }>>(
        `${this.apiUrl}/monthly-winloss`,
        { params }
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get monthly win/loss report');
    }

    return response.data;
  }

  /**
   * Get Personal Performance Review
   */
  async getPersonalPerformance(
    startDate?: Date,
    endDate?: Date,
    fsrId?: string
  ): Promise<PersonalPerformanceData> {
    let params = new HttpParams();

    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }

    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }

    if (fsrId) {
      params = params.set('fsrId', fsrId);
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<PersonalPerformanceData>>(
        `${this.apiUrl}/personal-performance`,
        { params }
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get personal performance');
    }

    return response.data;
  }

  /**
   * Export report
   */
  exportReport(reportType: string, format: 'csv' | 'excel' | 'pdf', additionalParams?: any): Observable<Blob> {
    let params = new HttpParams();
    
    if (additionalParams) {
      Object.keys(additionalParams).forEach(key => {
        params = params.set(key, additionalParams[key]);
      });
    }

    return this.http.get(
      `${this.apiUrl}/export/${reportType}/${format}`,
      { 
        params,
        responseType: 'blob'
      }
    );
  }

  /**
   * Get report permissions
   */
  async getReportPermissions(reportType: string): Promise<{ allowedRoles: string[] }> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ allowedRoles: string[] }>>(
        `${this.apiUrl}/permissions/${reportType}`
      )
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to get report permissions');
    }

    return response.data;
  }
}
